import type {
  AnnouncementPriority,
  AnnouncementRow,
} from "@/modules/announcements/domain/types";
import { formatDateLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import {
  DataTable,
  createSortableColumn,
  type TanstackReactTable,
} from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EditIcon } from "lucide-react";

const PRIORITY_LABELS: Record<AnnouncementPriority, string> = {
  NORMAL: "ທົ່ວໄປ",
  HIGH: "ສຳຄັນ",
  URGENT: "ດ່ວນ",
};

const PRIORITY_VARIANT: Record<
  AnnouncementPriority,
  "default" | "secondary" | "destructive"
> = {
  NORMAL: "secondary",
  HIGH: "default",
  URGENT: "destructive",
};

type AnnouncementsTableProps = {
  data: AnnouncementRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onEdit: (row: AnnouncementRow) => void;
};

export function AnnouncementsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onEdit,
}: AnnouncementsTableProps) {
  const columns: TanstackReactTable.ColumnDef<AnnouncementRow>[] = [
    {
      id: "image",
      header: "ຮູບ",
      size: 96,
      cell: ({ row }) => (
        <div className="aspect-4/3 w-20 shrink-0 overflow-hidden rounded-md border bg-muted">
          <AppImage
            src={row.original.imageUrl}
            alt=""
            className="size-full"
            fit="cover"
            showLoading={false}
            fallback={
              <div className="text-muted-foreground flex size-full items-center justify-center text-xs">
                —
              </div>
            }
          />
        </div>
      ),
      enableSorting: false,
    },
    createSortableColumn<AnnouncementRow>("title", "ຫົວຂໍ້", { size: 240 }),
    {
      id: "priority",
      header: "ລຳດັບ",
      size: 100,
      cell: ({ row }) => (
        <Badge variant={PRIORITY_VARIANT[row.original.priority]}>
          {PRIORITY_LABELS[row.original.priority]}
        </Badge>
      ),
      enableSorting: false,
    },
    createSortableColumn<AnnouncementRow>("publishAt", "ເຜີຍແຜ່", {
      size: 120,
      cell: ({ row }) => formatDateLocal(row.original.publishAt),
    }),
    {
      id: "expiresAt",
      header: "ໝົດອາຍຸ",
      size: 120,
      cell: ({ row }) =>
        row.original.expiresAt
          ? formatDateLocal(row.original.expiresAt)
          : "—",
      enableSorting: false,
    },
    {
      id: "showOnOpen",
      header: "Alert",
      size: 90,
      cell: ({ row }) => (
        <Badge variant={row.original.showOnOpen ? "default" : "outline"}>
          {row.original.showOnOpen ? "ເປີດ app" : "—"}
        </Badge>
      ),
      enableSorting: false,
    },
    {
      id: "sendPush",
      header: "Push",
      size: 100,
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5 text-xs">
          <Badge variant={row.original.sendPush ? "default" : "outline"}>
            {row.original.sendPush ? "ສົ່ງ" : "—"}
          </Badge>
          {row.original.pushSentAt ? (
            <span className="text-muted-foreground">
              {formatDateLocal(row.original.pushSentAt)}
            </span>
          ) : null}
        </div>
      ),
      enableSorting: false,
    },
    {
      id: "isActive",
      header: "ສະຖານະ",
      size: 110,
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? "default" : "secondary"}>
          {row.original.isActive ? "ເຜີຍແຜ່" : "ປິດ"}
        </Badge>
      ),
      enableSorting: false,
    },
    {
      id: "actions",
      header: "",
      size: 100,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(row.original)}
          title="ແກ້ໄຂ"
        >
          <EditIcon className="size-4" />
          ແກ້ໄຂ
        </Button>
      ),
      enableSorting: false,
    },
  ];

  return (
    <DataTable<AnnouncementRow, unknown>
      noDataMessage="ບໍ່ພົບຂ່າວສານ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
